import { basedOnType } from '../myFunctionsCoreDisplay';

// Reference tokens are `{{<key>.<field>}}` where <key> is the STABLE node id
// (preferred) or, for legacy data, a label-derived key. The authoring UI inserts
// id-based tokens; a label alias keeps legacy tokens resolving. These helpers
// power: (1) autocomplete suggestions that INSERT an id token but DISPLAY the
// node's friendly label, and (2) the read-only chip rendering of a field's text.

export interface ReferenceSuggestion {
  value: string; // inserted: `<nodeId>.<field>`
  display: string; // shown/filtered: `<label>.<field>`
}

export const labelKeyOf = (label?: string): string =>
  (label || '').replace(/\s/g, '').toLowerCase();

// Reference keys must be dot-free: the `{{key.field}}` token is resolved by
// dot-path, but legacy node ids embed a raw `Math.random()` float (e.g.
// `textBox_..._0.8148`), so the id itself can contain a `.`. Collapsing dots to
// `_` keeps the key a single path segment without losing uniqueness.
export const refKeyOf = (id?: string): string =>
  (id || '').replace(/\./g, '_');

type LiteNode = { id?: string; type?: string; data?: { label?: string } };

export function buildReferenceSuggestions(
  nodes: LiteNode[] | undefined,
): ReferenceSuggestion[] {
  const out: ReferenceSuggestion[] = [];
  for (const node of nodes || []) {
    if (!node?.id || node.type === 'globalStarter') continue;
    const fields = (basedOnType as Record<string, string[]>)[
      node.type as string
    ];
    if (!fields) continue;
    const label = node.data?.label || node.id;
    const refKey = refKeyOf(node.id);
    for (const field of fields) {
      out.push({ value: `${refKey}.${field}`, display: `${label}.${field}` });
    }
  }
  return out;
}

// Maps both the stable id AND the legacy label-key to the node's current label,
// so a chip can show the friendly (and always up-to-date) name for either token.
export function buildIdLabelMap(
  nodes: LiteNode[] | undefined,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const node of nodes || []) {
    if (!node?.id) continue;
    const label = node.data?.label || node.id;
    map[node.id] = label;
    const refKey = refKeyOf(node.id);
    if (refKey !== node.id) map[refKey] = label;
    const lk = labelKeyOf(node.data?.label);
    if (lk) map[lk] = label;
  }
  return map;
}

export interface RefSegment {
  type: 'text' | 'ref';
  raw: string;
  key?: string;
  field?: string;
}

export function splitReferenceSegments(text: unknown): RefSegment[] {
  if (typeof text !== 'string' || !text) {
    return [{ type: 'text', raw: typeof text === 'string' ? text : '' }];
  }
  const segments: RefSegment[] = [];
  const re = /\{\{(.*?)\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      segments.push({ type: 'text', raw: text.slice(last, m.index) });
    }
    const inner = (m[1] || '').trim();
    const dot = inner.indexOf('.');
    const key = dot >= 0 ? inner.slice(0, dot) : inner;
    const field = dot >= 0 ? inner.slice(dot + 1) : undefined;
    segments.push({ type: 'ref', raw: m[0], key, field });
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    segments.push({ type: 'text', raw: text.slice(last) });
  }
  return segments;
}

// Maps each label-key to the node ids that carry it, so a normalizer can tell a
// unique label (safe to rewrite to an id) from an ambiguous one (must stay).
export function buildLabelKeyToIds(
  nodes: LiteNode[] | undefined,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const node of nodes || []) {
    if (!node?.id) continue;
    const lk = labelKeyOf(node.data?.label);
    if (!lk) continue;
    (map[lk] ??= []).push(node.id);
  }
  return map;
}

export interface NormalizeReferencesResult {
  nodes: LiteNode[];
  rewrites: number;
  ambiguous: string[];
}

const rewriteTokenKeys = (
  text: string,
  resolveKey: (key: string) => string | null,
): { text: string; rewrites: number } => {
  let rewrites = 0;
  const next = text.replace(/\{\{(.*?)\}\}/g, (match, inner) => {
    const trimmed = (inner || '').trim();
    const dot = trimmed.indexOf('.');
    const key = dot >= 0 ? trimmed.slice(0, dot) : trimmed;
    const rest = dot >= 0 ? trimmed.slice(dot + 1) : '';
    const newKey = resolveKey(key);
    if (!newKey || newKey === key) return match;
    rewrites += 1;
    return `{{${newKey}${rest ? `.${rest}` : ''}}}`;
  });
  return { text: next, rewrites };
};

// Identity-preserving: returns the SAME reference when no token changed, so the
// caller can keep node objects stable and avoid spurious autosave/collab churn.
const rewriteDeep = (
  value: unknown,
  resolveKey: (key: string) => string | null,
  counter: { n: number },
): unknown => {
  if (typeof value === 'string') {
    if (!value.includes('{{')) return value;
    const { text, rewrites } = rewriteTokenKeys(value, resolveKey);
    counter.n += rewrites;
    return rewrites > 0 ? text : value;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map(item => {
      const next = rewriteDeep(item, resolveKey, counter);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : value;
  }
  if (value && typeof value === 'object') {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      const next = rewriteDeep(v, resolveKey, counter);
      if (next !== v) changed = true;
      out[k] = next;
    }
    return changed ? out : value;
  }
  return value;
};

// Best-effort, non-destructive normalization of legacy {{label.field}} tokens to
// the stable {{<refKey>.field}} form. Only UNIQUE labels are rewritten; ambiguous
// labels (shared by >1 node) and already-id tokens are left untouched so the
// dual-key resolver keeps handling them.
export function normalizeReferenceKeysInNodes(
  nodes: LiteNode[] | undefined,
): NormalizeReferencesResult {
  const list = nodes || [];
  const idSet = new Set<string>();
  const refKeySet = new Set<string>();
  for (const node of list) {
    if (!node?.id) continue;
    idSet.add(node.id);
    refKeySet.add(refKeyOf(node.id));
  }
  const labelKeyToIds = buildLabelKeyToIds(list);
  const ambiguous: string[] = [];
  const resolveKey = (key: string): string | null => {
    if (idSet.has(key) || refKeySet.has(key)) return null;
    const ids = labelKeyToIds[key];
    if (!ids) return null;
    if (ids.length > 1) {
      if (!ambiguous.includes(key)) ambiguous.push(key);
      return null;
    }
    return refKeyOf(ids[0]);
  };
  const counter = { n: 0 };
  const outNodes = list.map(node => {
    if (!node?.data) return node;
    const data = rewriteDeep(node.data, resolveKey, counter);
    return data === node.data ? node : { ...node, data };
  }) as LiteNode[];
  return { nodes: outNodes, rewrites: counter.n, ambiguous };
}

// Friendly label for a reference segment using the id→label map (falls back to
// the raw key when the referenced node no longer exists).
export function refSegmentLabel(
  seg: RefSegment,
  idLabelMap: Record<string, string>,
): string {
  const label = seg.key ? idLabelMap[seg.key] : undefined;
  const base = label ?? seg.key ?? '';
  return seg.field ? `${base}.${seg.field}` : base;
}
